import { httpBlob, httpJson } from '../../platform/abp/httpClient'
import { BUXUS_API_BASE_URL } from '../../platform/abp/config'
import { downloadBlob } from '../exportPreviewVideo'
import { CANNED_SCENARIOS } from '../canned'
import { createBlankBoardSession } from '../canned/blankBoard'
import { normalizePeople } from '../pagePeopleModel'
import type {
  ScenarioAction,
  ScenarioInstance,
  ScenarioPerson,
  ScenarioSession,
  ScenarioTemplate,
} from '../types'
import type { LoggedInPerson } from '../user'
import { parseAbsoluteInstant } from '../format'
import { studioConsoleVerbose } from '../pocStamp'
import { parseStudioClock, type StudioClock } from './scenarioClock'

export type ServerTemplateDto = {
  id: string
  templateKey: string
  title: string
  summary: string
  durationMs: number
  mapLabel: string
  hiddenFromTemplates: boolean
}

export type ServerSnapshot = {
  id: string
  templateId: string
  title: string
  /** Instance create time from ABP CreationTime (ISO). */
  creationTime?: string | null
  clock: StudioClock
  session: ScenarioSession
  people: ScenarioPerson[]
  /** Append-only room tape when the server column is present. */
  actions?: ScenarioAction[]
  /** Present once the host sends it. Omit means private. */
  audienceType?: 'public'
  /**
   * Anyone-with-the-link guests (not Public discovery). Org Buxus users with the
   * share URL may open without a roster seat — always read-only as guests.
   */
  allowLinkGuests?: boolean
  tenantId?: string | null
  creatorId?: string | null
  deleterId?: string | null
  deletionTime?: string | null
  archivedAt?: string | null
}

function parseAudienceType(raw: unknown): 'public' | undefined {
  return typeof raw === 'string' && raw.trim().toLowerCase() === 'public' ? 'public' : undefined
}

function unwrap<T>(body: unknown): T {
  const rec = body && typeof body === 'object' ? (body as Record<string, unknown>) : null
  if (rec && 'result' in rec && rec.result != null) return rec.result as T
  return body as T
}

function parseSession(raw: unknown): ScenarioSession {
  const text = typeof raw === 'string' ? raw : JSON.stringify(raw ?? {})
  return JSON.parse(text) as ScenarioSession
}

function parsePeople(raw: unknown): ScenarioPerson[] {
  const text = typeof raw === 'string' ? raw : JSON.stringify(raw ?? [])
  const parsed = JSON.parse(text) as unknown
  return normalizePeople(parsed)
}

function parseActions(raw: unknown): ScenarioAction[] {
  if (raw == null) return []
  try {
    const text = typeof raw === 'string' ? raw : JSON.stringify(raw ?? [])
    const parsed = JSON.parse(text) as unknown
    return Array.isArray(parsed) ? (parsed as ScenarioAction[]) : []
  } catch {
    return []
  }
}

function parseCreationTimeIso(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.trim()) return null
  const d = parseAbsoluteInstant(raw)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/** Earliest room-tape actualTime (hub writes UTC via Clock.Now.ToUniversalTime()). */
function firstActionActualIso(actions: ScenarioAction[] | undefined): string | null {
  if (!actions?.length) return null
  let best: string | null = null
  let bestMs = Infinity
  for (const row of actions) {
    const raw = row?.actualTime
    if (typeof raw !== 'string' || !raw.trim()) continue
    const ms = parseAbsoluteInstant(raw).getTime()
    if (!Number.isFinite(ms) || ms >= bestMs) continue
    bestMs = ms
    best = parseAbsoluteInstant(raw).toISOString()
  }
  return best
}

/**
 * Card / Oversight "when". ABP CreationTime is sometimes stored as local wall while the
 * room tape is true UTC — when tape is ≥1h ahead of CreationTime, trust the tape.
 * wizardOrigin is only a fallback (clones strip it server-side so it cannot stick).
 */
export function resolveInstanceCreatedAtIso(snap: ServerSnapshot): string {
  const creation = snap.creationTime ?? null
  const action = firstActionActualIso(snap.actions)
  const wizard = snap.session.wizardOrigin?.createdAtIso ?? null
  if (creation && action) {
    const c = parseAbsoluteInstant(creation).getTime()
    const a = parseAbsoluteInstant(action).getTime()
    if (Number.isFinite(c) && Number.isFinite(a) && a - c >= 3_600_000) return action
  }
  return creation ?? wizard ?? action ?? ''
}

export function snapshotFromWire(raw: unknown, _user: LoggedInPerson): ServerSnapshot {
  const body = unwrap<Record<string, unknown>>(raw)
  const clock = parseStudioClock(body.clock)
  const session = parseSession(body.sessionJson)
  if (typeof body.title === 'string' && body.title.trim()) session.title = body.title.trim()
  const actions = parseActions(body.actionsJson)
  return {
    id: String(body.id),
    templateId: String(body.templateId),
    title: session.title,
    creationTime: parseCreationTimeIso(body.creationTime ?? body.CreationTime),
    clock,
    session,
    people: parsePeople(body.peopleJson),
    ...(actions.length ? { actions } : {}),
    audienceType: parseAudienceType(body.audienceType),
    allowLinkGuests: body.allowLinkGuests === true,
    tenantId:
      typeof body.tenantId === 'string' && body.tenantId.trim()
        ? body.tenantId.trim()
        : null,
    creatorId:
      typeof body.creatorId === 'string' && body.creatorId.trim()
        ? body.creatorId.trim()
        : typeof body.CreatorId === 'string' && body.CreatorId.trim()
          ? body.CreatorId.trim()
          : null,
    deleterId:
      typeof body.deleterId === 'string' && body.deleterId.trim()
        ? body.deleterId.trim()
        : typeof body.DeleterId === 'string' && body.DeleterId.trim()
          ? body.DeleterId.trim()
          : null,
    deletionTime: parseCreationTimeIso(body.deletionTime ?? body.DeletionTime),
    archivedAt: parseCreationTimeIso(body.archivedAt ?? body.ArchivedAt),
  }
}

export function instanceFromSnapshot(snap: ServerSnapshot): ScenarioInstance {
  const playhead = snap.clock.anchorStoryTimeIso ?? snap.session.storyStartTime
  const createdAtIso = resolveInstanceCreatedAtIso(snap)
  return {
    id: snap.id,
    templateId: snap.templateId,
    lifecycle: snap.clock.lifecycle,
    session: snap.session,
    people: snap.people,
    ...(snap.actions?.length ? { actions: snap.actions } : {}),
    createdAtIso,
    actualStartTime: snap.clock.actualStartTime ?? undefined,
    actualEndTime: snap.clock.actualEndTime ?? undefined,
    playheadStoryIso: playhead,
    clockStatus: snap.clock.clockStatus,
    studioClock: snap.clock,
    ...(snap.audienceType === 'public' ? { audienceType: 'public' as const } : {}),
    ...(snap.allowLinkGuests ? { allowLinkGuests: true } : {}),
    ...(snap.tenantId ? { tenantId: snap.tenantId } : {}),
    ...(snap.creatorId ? { creatorId: snap.creatorId } : {}),
    ...(snap.deleterId ? { deleterId: snap.deleterId } : {}),
    ...(snap.deletionTime ? { deletionTimeIso: snap.deletionTime } : {}),
    ...(snap.archivedAt ? { archivedAtIso: snap.archivedAt } : {}),
  }
}

/** Catalog row. The list endpoint has no session JSON, so preview uses the local canned board for that templateKey. */
export function templateFromDto(dto: ServerTemplateDto): ScenarioTemplate {
  const canned = CANNED_SCENARIOS.find((row) => row.id === dto.templateKey)
  const session = canned ? canned.create() : createBlankBoardSession()
  session.id = dto.templateKey
  session.title = dto.title
  return {
    id: dto.id,
    title: dto.title,
    summary: dto.summary,
    durationMs: Number(dto.durationMs) || 0,
    mapLabel: dto.mapLabel,
    hiddenFromTemplates: dto.hiddenFromTemplates,
    session,
    updatedAtIso: new Date().toISOString(),
  }
}

export type ServerMapDto = {
  id: string
  mapKey: string
  title: string
  description?: string | null
  kind: string
  mapboxStyleUrl?: string | null
  isActive: boolean
  /** Public pk token when this style is not on the Sander Mapbox account. */
  mapboxAccessTokenOverride?: string | null
}

export async function fetchScenarioMaps(): Promise<ServerMapDto[]> {
  const raw = await httpJson<unknown>(`${BUXUS_API_BASE_URL}/scenarios/maps`, { auth: true })
  const list = unwrap<ServerMapDto[]>(raw)
  return Array.isArray(list) ? list : []
}

export async function fetchScenarioTemplates(): Promise<ScenarioTemplate[]> {
  const raw = await httpJson<unknown>(`${BUXUS_API_BASE_URL}/scenarios/templates`, { auth: true })
  const list = unwrap<ServerTemplateDto[]>(raw)
  return (Array.isArray(list) ? list : []).map(templateFromDto)
}

export async function createScenarioInstance(templateId: string, user: LoggedInPerson): Promise<ServerSnapshot> {
  const raw = await httpJson<unknown>(`${BUXUS_API_BASE_URL}/scenarios/instance`, {
    method: 'POST',
    auth: true,
    body: { templateId },
  })
  return snapshotFromWire(raw, user)
}

/** Planned board copy: seeds + settings; clears runs, tape, room chat; fresh roster. */
export async function cloneScenarioInstance(
  sourceInstanceId: string,
  user: LoggedInPerson
): Promise<ServerSnapshot> {
  const raw = await httpJson<unknown>(`${BUXUS_API_BASE_URL}/scenarios/instance/clone`, {
    method: 'POST',
    auth: true,
    body: { sourceInstanceId },
  })
  return snapshotFromWire(raw, user)
}

export type UiScale = 'sm' | 'md' | 'lg' | 'xl'

/** Named Studio color presets (not a binary light/dark toggle). */
export type StudioTheme = 'soft-blue' | 'steel' | 'amber'

const UI_SCALE_KEY = 'buxus.sc.uiScale'
const STUDIO_THEME_KEY = 'buxus.sc.studioTheme'

export function parseUiScale(value: unknown): UiScale {
  return value === 'sm' || value === 'lg' || value === 'xl' || value === 'md' ? value : 'md'
}

export function parseStudioTheme(value: unknown): StudioTheme {
  return value === 'soft-blue' || value === 'amber' || value === 'steel' ? value : 'soft-blue'
}

export function readUiScale(): UiScale {
  try {
    return parseUiScale(localStorage.getItem(UI_SCALE_KEY))
  } catch {
    return 'md'
  }
}

export function writeUiScale(scale: UiScale): void {
  try {
    localStorage.setItem(UI_SCALE_KEY, scale)
  } catch {
    /* private mode */
  }
}

export function readStudioTheme(): StudioTheme {
  try {
    return parseStudioTheme(localStorage.getItem(STUDIO_THEME_KEY))
  } catch {
    return 'soft-blue'
  }
}

export function writeStudioTheme(theme: StudioTheme): void {
  try {
    localStorage.setItem(STUDIO_THEME_KEY, theme)
  } catch {
    /* private mode */
  }
}

export async function fetchUserTextScale(): Promise<UiScale | null> {
  try {
    const raw = await httpJson<unknown>(`${BUXUS_API_BASE_URL}/scenarios/settings`, { auth: true })
    const body = unwrap<Record<string, unknown>>(raw)
    return parseUiScale(body?.textScale)
  } catch {
    return null
  }
}

export async function saveUserTextScale(scale: UiScale): Promise<void> {
  await httpJson(`${BUXUS_API_BASE_URL}/scenarios/settings`, {
    method: 'PUT',
    auth: true,
    body: { textScale: scale },
  })
}

export async function fetchUserStudioTheme(): Promise<StudioTheme | null> {
  try {
    // Prefer the full settings bag so older hosts (no studioTheme yet) still succeed.
    const raw = await httpJson<unknown>(`${BUXUS_API_BASE_URL}/scenarios/settings`, {
      auth: true,
    })
    const body = unwrap<Record<string, unknown>>(raw)
    return body?.studioTheme != null ? parseStudioTheme(body.studioTheme) : null
  } catch {
    return null
  }
}

export async function saveUserStudioTheme(theme: StudioTheme): Promise<void> {
  await httpJson(`${BUXUS_API_BASE_URL}/scenarios/settings`, {
    method: 'PUT',
    auth: true,
    body: { studioTheme: theme },
  })
}

const SERVER_INSTANCE_IDS_KEY = 'buxus.sc.serverInstanceIds'

export function readServerInstanceIds(): string[] {
  try {
    const raw = localStorage.getItem(SERVER_INSTANCE_IDS_KEY)
    const parsed = raw ? (JSON.parse(raw) as unknown) : []
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string' && id.length > 0) : []
  } catch {
    return []
  }
}

export function writeServerInstanceIds(ids: readonly string[]): void {
  try {
    localStorage.setItem(SERVER_INSTANCE_IDS_KEY, JSON.stringify([...new Set(ids)]))
  } catch {
    /* private mode */
  }
}

export async function fetchScenarioInstance(id: string, user: LoggedInPerson): Promise<ServerSnapshot> {
  const raw = await httpJson<unknown>(
    `${BUXUS_API_BASE_URL}/scenarios/instance/${encodeURIComponent(id)}`,
    { auth: true },
  )
  return snapshotFromWire(raw, user)
}

export async function fetchScenarioInstances(
  user: LoggedInPerson,
  opts?: { mutedOnly?: boolean },
): Promise<ServerSnapshot[]> {
  const q = opts?.mutedOnly ? '?mutedOnly=true' : ''
  const raw = await httpJson<unknown>(`${BUXUS_API_BASE_URL}/scenarios/instances${q}`, {
    auth: true,
  })
  const list = unwrap<unknown[]>(raw)
  return (Array.isArray(list) ? list : []).map((row) => snapshotFromWire(row, user))
}

/** Public Interest / discover shelf (show-first; excludes completed). */
export async function fetchDiscoverScenarioInstances(
  user: LoggedInPerson,
): Promise<ServerSnapshot[]> {
  const raw = await httpJson<unknown>(`${BUXUS_API_BASE_URL}/scenarios/instances/discover`, {
    auth: true,
  })
  const list = unwrap<unknown[]>(raw)
  return (Array.isArray(list) ? list : []).map((row) => snapshotFromWire(row, user))
}

/** Soft-deleted sessions this user may restore. */
export async function fetchDeletedScenarioInstances(
  user: LoggedInPerson,
): Promise<ServerSnapshot[]> {
  const raw = await httpJson<unknown>(`${BUXUS_API_BASE_URL}/scenarios/instances/deleted`, {
    auth: true,
  })
  const list = unwrap<unknown[]>(raw)
  return (Array.isArray(list) ? list : []).map((row) => snapshotFromWire(row, user))
}

/** Per-user mute — hide from my Sessions list (not soft-delete). */
export async function hideScenarioInstance(id: string): Promise<void> {
  await httpJson<unknown>(
    `${BUXUS_API_BASE_URL}/scenarios/instance/${encodeURIComponent(id)}/hide`,
    { method: 'POST', auth: true },
  )
}

export async function unhideScenarioInstance(id: string): Promise<void> {
  await httpJson<unknown>(
    `${BUXUS_API_BASE_URL}/scenarios/instance/${encodeURIComponent(id)}/unhide`,
    { method: 'POST', auth: true },
  )
}

export async function restoreDeletedScenarioInstance(
  id: string,
  user: LoggedInPerson,
): Promise<ServerSnapshot> {
  const raw = await httpJson<unknown>(
    `${BUXUS_API_BASE_URL}/scenarios/instance/${encodeURIComponent(id)}/restore-deleted`,
    { method: 'POST', auth: true },
  )
  return snapshotFromWire(raw, user)
}

export type InviteMaterialUpload = {
  fileName: string
  url: string
  sizeBytes: number
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = String(reader.result ?? '')
      const comma = dataUrl.indexOf(',')
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : '')
    }
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file.'))
    reader.readAsDataURL(file)
  })
}

/** Upload one desktop file for invite email links (long-lived read SAS). */
export async function uploadInviteMaterial(
  instanceId: string,
  file: File,
): Promise<InviteMaterialUpload> {
  const content = await fileToBase64(file)
  const raw = await httpJson<unknown>(
    `${BUXUS_API_BASE_URL}/scenarios/instance/${encodeURIComponent(instanceId)}/invite-materials`,
    {
      method: 'POST',
      auth: true,
      body: {
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        content,
      },
    },
  )
  const dto = unwrap<{ fileName?: string; url?: string; sizeBytes?: number }>(raw)
  const fileName = typeof dto.fileName === 'string' && dto.fileName.trim() ? dto.fileName.trim() : file.name
  const url = typeof dto.url === 'string' ? dto.url.trim() : ''
  if (!url.startsWith('https://')) {
    throw new Error('Invite material upload did not return a download URL.')
  }
  return {
    fileName,
    url,
    sizeBytes: typeof dto.sizeBytes === 'number' ? dto.sizeBytes : file.size,
  }
}

export type ScenarioStaticDocKind = 'cheatsheet' | 'trainer'

/** Fetch a static Studio doc PDF (QuestPDF) and trigger a browser file download. */
export async function fetchScenarioDocPdf(kind: ScenarioStaticDocKind): Promise<void> {
  const path = kind === 'trainer' ? 'docs/trainer-guide' : 'docs/cheatsheet'
  const fallback = kind === 'trainer' ? 'Trainer-Guide.pdf' : 'Product-Cheatsheet.pdf'
  const { blob, fileName } = await httpBlob(`${BUXUS_API_BASE_URL}/scenarios/${path}`, {
    auth: true,
  })
  downloadBlob(blob, fileName?.trim() || fallback)
}

export type ScenarioSessionPdfKind = 'plan' | 'summary' | 'aar' | 'template-plan'

/** Fetch a session PDF from the persisted instance and trigger a browser file download. */
export async function fetchScenarioInstancePdf(
  instanceId: string,
  kind: Exclude<ScenarioSessionPdfKind, 'template-plan'>,
): Promise<{ fileName: string; bytes: number }> {
  const id = instanceId.trim()
  if (!id) throw new Error('A session id is required.')
  const path =
    kind === 'summary' ? 'summary' : kind === 'aar' ? 'aar' : 'plan'
  const fallback =
    kind === 'summary'
      ? 'Run-Summary.pdf'
      : kind === 'aar'
        ? 'After-Action-Report.pdf'
        : 'Session-Plan.pdf'
  if (studioConsoleVerbose()) console.info('[ScenarioStudio] PDF request', kind, id)
  const { blob, fileName } = await httpBlob(
    `${BUXUS_API_BASE_URL}/scenarios/instance/${encodeURIComponent(id)}/docs/${path}`,
    { auth: true },
  )
  const name = fileName?.trim() || fallback
  downloadBlob(blob, name)
  if (studioConsoleVerbose()) console.info('[ScenarioStudio] PDF ok', kind, name, blob.size)
  return { fileName: name, bytes: blob.size }
}

/** @deprecated Prefer fetchScenarioInstancePdf(id, 'plan'). */
export async function fetchScenarioPlanPdf(instanceId: string): Promise<void> {
  await fetchScenarioInstancePdf(instanceId, 'plan')
}

/** Render a session PDF from a client snapshot (template plan) via QuestPDF. */
export async function fetchScenarioSnapshotPdf(input: {
  kind: ScenarioSessionPdfKind
  session: ScenarioSession
  people?: readonly ScenarioPerson[]
  title?: string
}): Promise<{ fileName: string; bytes: number }> {
  const kind = input.kind
  if (studioConsoleVerbose()) console.info('[ScenarioStudio] PDF request', kind, 'snapshot')
  const { blob, fileName } = await httpBlob(`${BUXUS_API_BASE_URL}/scenarios/docs/render`, {
    auth: true,
    method: 'POST',
    body: {
      kind,
      sessionJson: JSON.stringify(input.session),
      peopleJson: JSON.stringify(input.people ?? []),
      title: input.title?.trim() || input.session.title,
    },
  })
  const name =
    fileName?.trim() ||
    (kind === 'template-plan'
      ? 'Template-Plan.pdf'
      : kind === 'summary'
        ? 'Run-Summary.pdf'
        : kind === 'aar'
          ? 'After-Action-Report.pdf'
          : 'Session-Plan.pdf')
  downloadBlob(blob, name)
  if (studioConsoleVerbose()) console.info('[ScenarioStudio] PDF ok', kind, name, blob.size)
  return { fileName: name, bytes: blob.size }
}
